import { DefaultValue } from 'recoil';
import { atom, atomFamily, selector, selectorFamily } from 'recoil';
import { freightTableState, shippingTableState } from './shipping';
import { getCaddyCost, getOrganizerCost, getSpiceRackCost } from './calculations';
import { getMeasurementInUnit, roundHalf, findClosestIndex } from '@utils';

let lookupHeightTable = [2, 2.5, 3, 3.5, 4, 4.5, 5];
let lookupHeightTableValues = [2, 3, 4, 5, 6, 7, 8];

export const selectedVariantState = atom({
  key: 'selectedVariant',
  default: null,
});

export const baseProductState = atom({
  key: 'baseProduct',
  default: null,
});

export const addOnProductState = atom({
  key: 'addOnProduct',
  default: null,
});

export const measurementsState = atomFamily({
  key: 'measurementsState',
});

export const calculationSpecs = atom({
  key: 'calculationSpecs',
  default: null,
});

export const baseProductSpecs = atom({
  key: 'baseProductSpecs',
  default: null,
});

export const baseUnitState = atom({
  key: 'baseUnit',
  default: 'imperial',
  effects_UNSTABLE: [
    ({ onSet, setSelf }) => {
      const storedValue = localStorage.getItem('sbs_baseUnit');
      let params = new URLSearchParams(window.location.search);
      let valueToLoad = params.get('_userUnit');
      if (valueToLoad) {
        setSelf(valueToLoad);
      } else {
        if (storedValue != null) {
          setSelf(storedValue);
        }
      }
      onSet((newValue) => {
        if (newValue instanceof DefaultValue) {
          localStorage.removeItem('sbs_baseUnit');
        } else {
          localStorage.setItem('sbs_baseUnit', newValue);
        }
      });
    },
  ],
});

export const productMeasurements = selector({
  key: 'produtMeasurements',
  get: ({ get }) => {
    let freight = get(freightTableState);
    let packaging = get(shippingTableState);
    let length = get(measurementsState('length'));
    let width = get(measurementsState('width'));
    let height = get(measurementsState('height'));
    let baseUnit = get(baseUnitState);
    let specs = get(calculationSpecs);

    let productType = specs?.product_type?.name?.toLowerCase();

    const calculationFormulas = {
      organizers: getOrganizerCost,
      'medical organizers': getOrganizerCost,
      trays: getOrganizerCost,
      caddies: getCaddyCost,
      'spice racks': getSpiceRackCost,
    };

    if (!specs || !packaging || !productType) {
      return null;
    }

    let getBuildCost = calculationFormulas[productType];

    let surfaceArea = width * length;
    let configCost = getBuildCost({ length, width, height, specs, baseUnit, freight, packaging, retail: true });
    let factoryCost = getBuildCost({ length, width, height, specs, baseUnit, freight, packaging, retail: false });

    var maximumHeight;

    if (productType === 'organizers') {
      let maxH = 8;
      let length = baseUnit === 'imperial' ? Number(specs.sizes[0].length.max) : Number(specs.sizes[0].length.max * 25.4);
      let width = baseUnit === 'imperial' ? Number(specs.sizes[0].width.max) : Number(specs.sizes[0].width.max * 25.4);
      let lookupValue = Math.min(
        getMeasurementInUnit(width, baseUnit, 'imperial') * specs.sizes[0].smallest_compartment.w,
        getMeasurementInUnit(length, baseUnit, 'imperial') * specs.sizes[0].smallest_compartment.l
      );
      let maxHeight = roundHalf(Math.min(lookupHeightTableValues[findClosestIndex(lookupHeightTable, roundHalf(lookupValue))], maxH));
      maximumHeight = getMeasurementInUnit(maxHeight, 'imperial', baseUnit);
    } else {
      maximumHeight = baseUnit === 'imperial' ? Number(specs.sizes[0].height.max) : Number(specs.sizes[0].height.max * 25.4);
    }

    let minCost = getBuildCost({
      length: baseUnit === 'imperial' ? Number(specs.sizes[0].length.min) : Number(specs.sizes[0].length.min * 25.4),
      width: baseUnit === 'imperial' ? Number(specs.sizes[0].width.min) : Number(specs.sizes[0].width.min * 25.4),
      height: baseUnit === 'imperial' ? Number(specs.sizes[0].height.min) : Number(specs.sizes[0].height.min * 25.4),
      specs,
      baseUnit,
      freight,
      packaging,
      retail: true,
    });

    // let minCost = 0;

    // let maxCost = getBuildCost({
    //   length: baseUnit === 'imperial' ? Number(specs.length.max) : Number(specs.length.max * 25.4),
    //   width: baseUnit === 'imperial' ? Number(specs.width.max) : Number(specs.width.max * 25.4),
    //   height: maximumHeight,
    //   specs,
    //   baseUnit,
    //   freight,
    //   packaging,
    //   retail: true,
    // });

    return { length, width, height, surfaceArea, configCost, minCost, factoryCost };
  },
});

export const configurationComplete = selector({
  key: 'configurationComplete',
  get: ({ get }) => {
    let length = get(measurementsState('length'));
    let width = get(measurementsState('width'));
    let height = get(measurementsState('height'));
    let roomName = get(measurementsState('roomName'));
    let baseUnit = get(baseUnitState);
    let configData = { length, width, height, baseUnit, roomName };
    let isComplete = Object.keys(configData).every((key) => configData[key] != null && configData[key] != '');
    return { isComplete, data: { length, width, height, baseUnit, roomName } };
  },
});
